/**
 * ClientPicker - Wrapper around generic EntityPicker
 * 
 * Provides client-specific configuration and maintains backward compatibility
 * with any code that imports ClientPickerField or ClientPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';

// ── Client-specific configuration ────────────────────────────────────────────
export const CLIENT_CONFIG = {
  apiEndpoint: "/api/Client/active",
  entityName: "Client",
  idField: "id",
  displayFields: {
    primary: "description"
  },
  tableHeaders: ["Description"],
  tableAccessors: ["description"],
  searchFields: [
    { label: "Description", field: "description", index: 0 },
  ],
  placeholder: "--Select client--"
};

// ── ClientPickerField wrapper ────────────────────────────────────────────────
/**
 * ClientPickerField
 * 
 * Usage:
 * <ClientPickerField
 *   label="Client"
 *   clientId={form.clientId}
 *   displayValue={form.clientDescription}
 *   onSelect={(client) => {
 *     set("clientId", client.id);
 *     set("clientDescription", client.description);
 *   }}
 *   onClear={() => {
 *     set("clientId", "");
 *     set("clientDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function ClientPickerField({
  clientId,           // client-specific prop name
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  return (
    <EntityPickerField
      {...rest}
      config={CLIENT_CONFIG}
      entityId={clientId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── ClientPickerModal wrapper ────────────────────────────────────────────────
/**
 * ClientPickerModal
 * 
 * Usage (standalone):
 * <ClientPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(client) => {
 *     set("clientId", client.id);
 *     set("clientDescription", client.description);
 *   }}
 * />
 */
export function ClientPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={CLIENT_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { ClientPickerField as default };